import {
  type CSSProperties,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Locale } from "./i18n";

type Copy = (key: string, values?: Record<string, string | number>) => string;
type QrStyle = "square" | "rounded" | "dots";
type Category = "cafe_tea" | "food" | "shops";

export interface MerchantProfile {
  merchantId: string;
  slug: string;
  discoveryStatus: "unlisted" | "listed";
  category: Category;
  websiteUrl: string | null;
  brandColor: string;
  logoUrl: string | null;
  localizations: {
    en: { displayName: string; shortDescription: string | null };
    "zh-Hant"?: { displayName: string; shortDescription: string | null };
  };
  qr: {
    foreground: string;
    background: string;
    moduleStyle: QrStyle;
    logoEnabled: boolean;
  };
}

export interface MerchantProfileDraft {
  category: Category;
  websiteUrl: string;
  brandColor: string;
  displayNameEn: string;
  descriptionEn: string;
  displayNameZhHant: string;
  descriptionZhHant: string;
  qrForeground: string;
  qrBackground: string;
  qrModuleStyle: QrStyle;
  qrLogoEnabled: boolean;
}

export interface MerchantProfileInput {
  category: Category;
  websiteUrl: string | null;
  brandColor: string;
  localizations: MerchantProfile["localizations"];
  qr: MerchantProfile["qr"];
}

type LoadState =
  | { status: "loading" }
  | { status: "error"; permission: boolean }
  | { status: "ready"; profile: MerchantProfile };

const HEX_COLOR = /^#[0-9A-F]{6}$/;

function profileDraft(profile: MerchantProfile): MerchantProfileDraft {
  return {
    category: profile.category,
    websiteUrl: profile.websiteUrl ?? "",
    brandColor: profile.brandColor,
    displayNameEn: profile.localizations.en.displayName,
    descriptionEn: profile.localizations.en.shortDescription ?? "",
    displayNameZhHant: profile.localizations["zh-Hant"]?.displayName ?? "",
    descriptionZhHant: profile.localizations["zh-Hant"]?.shortDescription ?? "",
    qrForeground: profile.qr.foreground,
    qrBackground: profile.qr.background,
    qrModuleStyle: profile.qr.moduleStyle,
    qrLogoEnabled: profile.qr.logoEnabled,
  };
}

export function normalizeMerchantProfileDraft(
  draft: MerchantProfileDraft,
): MerchantProfileInput | null {
  const websiteUrl = draft.websiteUrl.trim();
  const englishName = draft.displayNameEn.trim();
  const chineseName = draft.displayNameZhHant.trim();
  const brandColor = draft.brandColor.trim().toUpperCase();
  const foreground = draft.qrForeground.trim().toUpperCase();
  const background = draft.qrBackground.trim().toUpperCase();
  if (
    englishName.length === 0 ||
    englishName.length > 140 ||
    draft.descriptionEn.trim().length > 280 ||
    chineseName.length > 140 ||
    draft.descriptionZhHant.trim().length > 280 ||
    (draft.descriptionZhHant.trim().length > 0 && chineseName.length === 0) ||
    !HEX_COLOR.test(brandColor) ||
    !HEX_COLOR.test(foreground) ||
    !HEX_COLOR.test(background)
  ) {
    return null;
  }
  if (websiteUrl) {
    try {
      if (new URL(websiteUrl).protocol !== "https:") return null;
    } catch {
      return null;
    }
  }
  return {
    category: draft.category,
    websiteUrl: websiteUrl || null,
    brandColor,
    localizations: {
      en: {
        displayName: englishName,
        shortDescription: draft.descriptionEn.trim() || null,
      },
      ...(chineseName
        ? {
            "zh-Hant": {
              displayName: chineseName,
              shortDescription: draft.descriptionZhHant.trim() || null,
            },
          }
        : {}),
    },
    qr: {
      foreground,
      background,
      moduleStyle: draft.qrModuleStyle,
      logoEnabled: draft.qrLogoEnabled,
    },
  };
}

function isProfile(value: unknown): value is MerchantProfile {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const profile = value as Record<string, unknown>;
  return (
    typeof profile.merchantId === "string" &&
    typeof profile.slug === "string" &&
    (profile.discoveryStatus === "unlisted" ||
      profile.discoveryStatus === "listed") &&
    typeof profile.brandColor === "string" &&
    Boolean(profile.localizations) &&
    typeof profile.localizations === "object" &&
    Boolean(profile.qr) &&
    typeof profile.qr === "object"
  );
}

function idempotencyKey(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
}

export function ShopProfile({
  merchantId,
  canManage,
  locale,
  copy,
  onSavedName,
  initialProfile,
}: {
  merchantId: string;
  canManage: boolean;
  locale: Locale;
  copy: Copy;
  onSavedName?: (name: string) => void;
  initialProfile?: MerchantProfile;
}) {
  const [state, setState] = useState<LoadState>(
    initialProfile
      ? { status: "ready", profile: initialProfile }
      : { status: "loading" },
  );
  const [draft, setDraft] = useState<MerchantProfileDraft | null>(
    initialProfile ? profileDraft(initialProfile) : null,
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<"validation" | "request" | null>(
    null,
  );
  const [saved, setSaved] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [qrPreview, setQrPreview] = useState<string | null>(null);
  const [qrPreviewError, setQrPreviewError] = useState(false);
  const [discoverySaving, setDiscoverySaving] = useState(false);
  const [discoveryError, setDiscoveryError] = useState<
    "not-ready" | "request" | null
  >(null);
  const [discoverySaved, setDiscoverySaved] = useState<
    "unlisted" | "listed" | null
  >(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const response = await fetch("/v1/merchant/profile", {
        credentials: "include",
        headers: {
          "accept-language": locale,
          "x-merchant-id": merchantId,
        },
      });
      if (!response.ok) {
        setState({
          status: "error",
          permission: response.status === 401 || response.status === 403,
        });
        return;
      }
      const value: unknown = await response.json();
      if (!isProfile(value)) {
        setState({ status: "error", permission: false });
        return;
      }
      setState({ status: "ready", profile: value });
      setDraft(profileDraft(value));
    } catch {
      setState({ status: "error", permission: false });
    }
  }, [locale, merchantId]);

  useEffect(() => {
    if (initialProfile) return;
    void load();
  }, [initialProfile, load]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  useEffect(() => {
    if (!draft || state.status !== "ready") return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({
        foreground: draft.qrForeground,
        background: draft.qrBackground,
        moduleStyle: draft.qrModuleStyle,
        logoEnabled: String(draft.qrLogoEnabled),
      });
      fetch(`/v1/merchant/profile/qr-preview.svg?${params.toString()}`, {
        credentials: "include",
        headers: { "x-merchant-id": merchantId },
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("preview unavailable");
          return response.blob();
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          setQrPreview((current) => {
            if (current) URL.revokeObjectURL(current);
            return url;
          });
          setQrPreviewError(false);
        })
        .catch((error: unknown) => {
          if (!(error instanceof DOMException && error.name === "AbortError")) {
            setQrPreviewError(true);
          }
        });
    }, 180);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [draft, merchantId, state.status]);

  useEffect(
    () => () => {
      if (qrPreview) URL.revokeObjectURL(qrPreview);
    },
    [qrPreview],
  );

  const update = <Key extends keyof MerchantProfileDraft>(
    key: Key,
    value: MerchantProfileDraft[Key],
  ) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current));

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    const input = normalizeMerchantProfileDraft(draft);
    if (!input) {
      setSaveError("validation");
      return;
    }
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const response = await fetch("/v1/merchant/profile", {
        method: "PUT",
        credentials: "include",
        headers: {
          "accept-language": locale,
          "content-type": "application/json",
          "idempotency-key": idempotencyKey("idem_merchant_profile_ui"),
          "x-merchant-id": merchantId,
        },
        body: JSON.stringify(input),
      });
      const value: unknown = await response.json().catch(() => null);
      if (!response.ok || !isProfile(value)) {
        setSaveError("request");
        return;
      }
      setState({ status: "ready", profile: value });
      setDraft(profileDraft(value));
      setSaved(true);
      onSavedName?.(value.localizations.en.displayName);
    } catch {
      setSaveError("request");
    } finally {
      setSaving(false);
    }
  };

  const saveLogo = async () => {
    if (!logoFile || state.status !== "ready") return;
    if (
      !["image/png", "image/jpeg", "image/webp"].includes(logoFile.type) ||
      logoFile.size > 512 * 1024
    ) {
      setLogoError(true);
      return;
    }
    setLogoSaving(true);
    setLogoError(false);
    try {
      const response = await fetch("/v1/merchant/profile/logo", {
        method: "PUT",
        credentials: "include",
        headers: {
          "content-type": logoFile.type,
          "idempotency-key": idempotencyKey("idem_merchant_logo_ui"),
          "x-merchant-id": merchantId,
        },
        body: logoFile,
      });
      const value: unknown = await response.json().catch(() => null);
      if (!response.ok || !isProfile(value)) {
        setLogoError(true);
        return;
      }
      setState({ status: "ready", profile: value });
      setDraft(profileDraft(value));
      setLogoFile(null);
    } catch {
      setLogoError(true);
    } finally {
      setLogoSaving(false);
    }
  };

  const updateDiscovery = async (
    discoveryStatus: MerchantProfile["discoveryStatus"],
  ) => {
    if (state.status !== "ready") return;
    setDiscoverySaving(true);
    setDiscoveryError(null);
    setDiscoverySaved(null);
    try {
      const response = await fetch("/v1/merchant/discovery", {
        method: "PUT",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "idempotency-key": idempotencyKey("idem_merchant_discovery_ui"),
          "x-merchant-id": merchantId,
        },
        body: JSON.stringify({ discoveryStatus }),
      });
      const value: unknown = await response.json().catch(() => null);
      if (!response.ok || !isProfile(value)) {
        const code =
          value && typeof value === "object" && !Array.isArray(value)
            ? (value as { error?: { code?: unknown } }).error?.code
            : null;
        setDiscoveryError(
          code === "MERCHANT_DISCOVERY_NOT_READY" ? "not-ready" : "request",
        );
        return;
      }
      setState({ status: "ready", profile: value });
      setDraft(profileDraft(value));
      setDiscoverySaved(value.discoveryStatus);
    } catch {
      setDiscoveryError("request");
    } finally {
      setDiscoverySaving(false);
    }
  };

  if (state.status === "loading") {
    return (
      <section className="panel operations-status" aria-busy="true">
        <div className="loading-mark" aria-hidden="true" />
        <h2>{copy("merchant.shop.loading")}</h2>
      </section>
    );
  }
  if (state.status === "error") {
    return (
      <section className="panel empty-state" role="alert">
        <span className="empty-mark" aria-hidden="true" />
        <h2>
          {copy(
            state.permission
              ? "merchant.shop.permissionTitle"
              : "merchant.shop.errorTitle",
          )}
        </h2>
        <p>
          {copy(
            state.permission
              ? "merchant.shop.permissionBody"
              : "merchant.shop.errorBody",
          )}
        </p>
        {!state.permission && (
          <button
            className="primary-button"
            type="button"
            onClick={() => void load()}
          >
            {copy("common.retry")}
          </button>
        )}
      </section>
    );
  }
  if (!draft) {
    return (
      <section className="panel empty-state" role="alert">
        <h2>{copy("merchant.shop.errorTitle")}</h2>
        <p>{copy("merchant.shop.errorBody")}</p>
        <button
          className="primary-button"
          type="button"
          onClick={() => void load()}
        >
          {copy("common.retry")}
        </button>
      </section>
    );
  }

  const visibleName =
    locale === "zh-Hant" && draft.displayNameZhHant.trim()
      ? draft.displayNameZhHant
      : draft.displayNameEn;
  const visibleDescription =
    locale === "zh-Hant" && draft.descriptionZhHant.trim()
      ? draft.descriptionZhHant
      : draft.descriptionEn;
  const previewLogo = logoPreview ?? state.profile.logoUrl;
  const previewStyle = {
    "--merchant-brand": draft.brandColor,
  } as CSSProperties;

  return (
    <form
      className="shop-profile"
      onSubmit={(event) => void saveProfile(event)}
    >
      <section className="panel shop-identity-editor">
        <div className="section-heading shop-section-heading">
          <div>
            <p className="eyebrow">{copy("merchant.shop.identityEyebrow")}</p>
            <h2>{copy("merchant.shop.identityTitle")}</h2>
            <p>{copy("merchant.shop.identityBody")}</p>
          </div>
        </div>
        <div className="logo-editor">
          <div className="merchant-logo-preview" style={previewStyle}>
            {previewLogo ? (
              <img src={previewLogo} alt="" />
            ) : (
              <span aria-hidden="true">{draft.displayNameEn.slice(0, 1)}</span>
            )}
          </div>
          <div>
            <label className="file-button" htmlFor="merchant-logo">
              {copy("merchant.shop.chooseLogo")}
            </label>
            <input
              className="visually-hidden"
              id="merchant-logo"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={!canManage || logoSaving}
              onChange={(event) => setLogoFile(event.target.files?.[0] ?? null)}
            />
            <p>{copy("merchant.shop.logoHelp")}</p>
            {logoFile && (
              <button
                className="text-button logo-save-button"
                type="button"
                disabled={!canManage || logoSaving}
                onClick={() => void saveLogo()}
              >
                {copy(
                  logoSaving
                    ? "merchant.shop.logoSaving"
                    : "merchant.shop.logoSave",
                )}
              </button>
            )}
            {logoError && (
              <p className="form-error" role="alert">
                {copy("merchant.shop.logoError")}
              </p>
            )}
          </div>
        </div>
        <fieldset className="localization-fields">
          <legend>{copy("merchant.shop.english")}</legend>
          <ProfileField
            id="shop-name-en"
            label={copy("merchant.shop.displayName")}
            value={draft.displayNameEn}
            maxLength={140}
            disabled={!canManage}
            onChange={(value) => update("displayNameEn", value)}
          />
          <ProfileField
            id="shop-description-en"
            label={copy("merchant.shop.description")}
            value={draft.descriptionEn}
            maxLength={280}
            multiline
            required={false}
            disabled={!canManage}
            onChange={(value) => update("descriptionEn", value)}
          />
        </fieldset>
        <fieldset className="localization-fields">
          <legend>{copy("merchant.shop.traditionalChinese")}</legend>
          <ProfileField
            id="shop-name-zh"
            label={copy("merchant.shop.displayName")}
            value={draft.displayNameZhHant}
            maxLength={140}
            required={false}
            disabled={!canManage}
            onChange={(value) => update("displayNameZhHant", value)}
          />
          <ProfileField
            id="shop-description-zh"
            label={copy("merchant.shop.description")}
            value={draft.descriptionZhHant}
            maxLength={280}
            multiline
            required={false}
            disabled={!canManage}
            onChange={(value) => update("descriptionZhHant", value)}
          />
        </fieldset>
        <div className="shop-details-grid">
          <label>
            <span>{copy("merchant.shop.category")}</span>
            <select
              value={draft.category}
              disabled={!canManage}
              onChange={(event) =>
                update("category", event.target.value as Category)
              }
            >
              <option value="cafe_tea">
                {copy("merchant.shop.categories.cafe_tea")}
              </option>
              <option value="food">
                {copy("merchant.shop.categories.food")}
              </option>
              <option value="shops">
                {copy("merchant.shop.categories.shops")}
              </option>
            </select>
          </label>
          <ProfileField
            id="shop-website"
            label={copy("merchant.shop.website")}
            value={draft.websiteUrl}
            type="url"
            required={false}
            disabled={!canManage}
            placeholder="https://"
            onChange={(value) => update("websiteUrl", value)}
          />
          <ColorField
            id="shop-brand-color"
            label={copy("merchant.shop.brandColor")}
            value={draft.brandColor}
            disabled={!canManage}
            onChange={(value) => update("brandColor", value)}
          />
        </div>
      </section>

      <aside className="shop-profile-preview-column">
        <section className="panel discovery-control">
          <div className="section-heading shop-section-heading">
            <div>
              <p className="eyebrow">
                {copy("merchant.shop.discoveryEyebrow")}
              </p>
              <h2>{copy("merchant.shop.discoveryTitle")}</h2>
              <p>
                {copy(
                  state.profile.discoveryStatus === "listed"
                    ? "merchant.shop.discoveryListedBody"
                    : "merchant.shop.discoveryUnlistedBody",
                )}
              </p>
            </div>
            <span
              className={`discovery-status ${state.profile.discoveryStatus}`}
            >
              <i aria-hidden="true" />
              {copy(
                state.profile.discoveryStatus === "listed"
                  ? "merchant.shop.discoveryListed"
                  : "merchant.shop.discoveryUnlisted",
              )}
            </span>
          </div>
          <button
            className={
              state.profile.discoveryStatus === "listed"
                ? "quiet-button"
                : "primary-button"
            }
            type="button"
            disabled={!canManage || discoverySaving}
            onClick={() =>
              void updateDiscovery(
                state.profile.discoveryStatus === "listed"
                  ? "unlisted"
                  : "listed",
              )
            }
          >
            {copy(
              discoverySaving
                ? state.profile.discoveryStatus === "listed"
                  ? "merchant.shop.discoveryUnlisting"
                  : "merchant.shop.discoveryListing"
                : state.profile.discoveryStatus === "listed"
                  ? "merchant.shop.discoveryUnlist"
                  : "merchant.shop.discoveryList",
            )}
          </button>
          <div className="discovery-feedback" aria-live="polite">
            {discoverySaved && (
              <span className="save-success">
                {copy(
                  discoverySaved === "listed"
                    ? "merchant.shop.discoveryListedSaved"
                    : "merchant.shop.discoveryUnlistedSaved",
                )}
              </span>
            )}
            {discoveryError && (
              <span className="form-error" role="alert">
                {copy(
                  discoveryError === "not-ready"
                    ? "merchant.shop.discoveryNotReady"
                    : "merchant.shop.discoveryError",
                )}
              </span>
            )}
            {!canManage && (
              <span>{copy("merchant.shop.discoveryReadOnly")}</span>
            )}
          </div>
        </section>

        <section className="panel customer-preview" style={previewStyle}>
          <p className="eyebrow">{copy("merchant.shop.customerPreview")}</p>
          <div className="customer-preview-card">
            <div className="merchant-logo-preview small">
              {previewLogo ? (
                <img src={previewLogo} alt="" />
              ) : (
                <span>{draft.displayNameEn.slice(0, 1)}</span>
              )}
            </div>
            <div>
              <h2>{visibleName || copy("merchant.shop.previewFallback")}</h2>
              <p>
                {visibleDescription ||
                  copy("merchant.shop.previewDescriptionFallback")}
              </p>
            </div>
          </div>
          <div className="preview-wallet-action">
            <span aria-hidden="true">+</span>
            {copy("merchant.shop.previewWallet")}
          </div>
          <p className="preview-note">
            {copy("merchant.shop.previewSurfaces")}
          </p>
        </section>

        <section className="panel qr-style-editor">
          <div className="section-heading shop-section-heading">
            <div>
              <p className="eyebrow">{copy("merchant.shop.qrEyebrow")}</p>
              <h2>{copy("merchant.shop.qrTitle")}</h2>
              <p>{copy("merchant.shop.qrBody")}</p>
            </div>
          </div>
          <div
            className="qr-live-preview"
            style={{ background: draft.qrBackground }}
          >
            {qrPreview ? (
              <img src={qrPreview} alt={copy("merchant.shop.qrPreviewAlt")} />
            ) : (
              <div className="qr-preview-placeholder" aria-busy="true" />
            )}
          </div>
          {qrPreviewError && (
            <p className="form-error" role="alert">
              {copy("merchant.shop.qrPreviewError")}
            </p>
          )}
          <div className="qr-controls">
            <ColorField
              id="qr-foreground"
              label={copy("merchant.shop.qrForeground")}
              value={draft.qrForeground}
              disabled={!canManage}
              onChange={(value) => update("qrForeground", value)}
            />
            <ColorField
              id="qr-background"
              label={copy("merchant.shop.qrBackground")}
              value={draft.qrBackground}
              disabled={!canManage}
              onChange={(value) => update("qrBackground", value)}
            />
            <fieldset className="qr-shape-picker">
              <legend>{copy("merchant.shop.qrShape")}</legend>
              <div>
                {(["square", "rounded", "dots"] as const).map((style) => (
                  <label key={style}>
                    <input
                      type="radio"
                      name="qr-style"
                      checked={draft.qrModuleStyle === style}
                      disabled={!canManage}
                      onChange={() => update("qrModuleStyle", style)}
                    />
                    <span
                      className={`qr-shape-swatch ${style}`}
                      aria-hidden="true"
                    />
                    {copy(`merchant.shop.qrStyles.${style}`)}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="toggle-row">
              <span>
                <strong>{copy("merchant.shop.qrLogo")}</strong>
                <small>{copy("merchant.shop.qrLogoHelp")}</small>
              </span>
              <input
                type="checkbox"
                checked={draft.qrLogoEnabled}
                disabled={!canManage || !state.profile.logoUrl}
                onChange={(event) =>
                  update("qrLogoEnabled", event.target.checked)
                }
              />
            </label>
          </div>
          <p className="qr-guardrail">{copy("merchant.shop.qrGuardrail")}</p>
        </section>
      </aside>

      <div className="shop-save-bar">
        <div aria-live="polite">
          {saved && (
            <span className="save-success">{copy("merchant.shop.saved")}</span>
          )}
          {saveError && (
            <span className="form-error" role="alert">
              {copy(
                saveError === "validation"
                  ? "merchant.shop.validation"
                  : "merchant.shop.saveError",
              )}
            </span>
          )}
          {!canManage && <span>{copy("merchant.shop.readOnly")}</span>}
        </div>
        <button
          className="primary-button"
          type="submit"
          disabled={!canManage || saving}
        >
          {copy(saving ? "merchant.shop.saving" : "merchant.shop.save")}
        </button>
      </div>
    </form>
  );
}

function ProfileField({
  id,
  label,
  value,
  onChange,
  required = true,
  maxLength,
  multiline = false,
  type = "text",
  placeholder,
  disabled = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  maxLength?: number;
  multiline?: boolean;
  type?: "text" | "url";
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div className="field-control">
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          required={required}
          maxLength={maxLength}
          rows={3}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          required={required}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {maxLength && (
        <span className="field-meta">
          {value.length}/{maxLength}
        </span>
      )}
    </div>
  );
}

function ColorField({
  id,
  label,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="color-field" htmlFor={id}>
      <span>{label}</span>
      <span className="color-field-control">
        <input
          id={id}
          type="color"
          value={HEX_COLOR.test(value.toUpperCase()) ? value : "#000000"}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
        />
        <code>{value.toUpperCase()}</code>
      </span>
    </label>
  );
}
